import { Capacitor } from '@capacitor/core'
import { v4 as uuidv4 } from 'uuid'
import Activity from '../../model/Activity'
import ActivityLog from '../../model/ActivityLog'
import Message from '../../model/Message'
import Notification, { NotificationType } from '../../model/Notification'
import User from '../../model/User'
import { logError } from '../../util/errorLogger'
import { dataSource, edgeFunctionService } from './_service.config'

interface CreateNotificationParams {
  userId: string
  type: NotificationType
  title: string
  message: string
  data?: Record<string, any>
  sendPush?: boolean
}

export interface SendNotificationResult {
  notification: Notification
  pushFailed: boolean
}

export default class NotificationService {
  async sendNotification({
    userId,
    type,
    title,
    message,
    data = {},
    sendPush = false,
  }: CreateNotificationParams): Promise<SendNotificationResult> {
    const notification = new Notification({
      id: uuidv4(),
      userId,
      type,
      title,
      message,
      data,
      read: false,
      createdAt: new Date(),
      sendPush,
    })

    // Save the notification
    try {
      await dataSource.saveNotification(notification)
    } catch (error) {
      void logError(error, { service: 'NotificationService', action: 'saveNotification', userId })
    }

    // Only set pushFailed when the edge function explicitly returns success: false.
    // When the client gets `error` (e.g. CORS blocked reading the 200 response), we don't
    // set pushFailed, so we avoid showing a false "some teammates didn't get the notification" warning.
    let pushFailed = false
    if (sendPush && Capacitor.getPlatform() !== 'android') {
      try {
        const { data: fnData, error } = await edgeFunctionService.invoke<{ success?: boolean; error?: string }>(
          'send-push-notification',
          { notification: notification.toPG() },
        )

        if (error) {
          void logError(error, { service: 'NotificationService', action: 'sendPush', userId })
        }
        if (fnData && fnData.success === false) {
          void logError(new Error(fnData.error || 'Unknown error'), { service: 'NotificationService', action: 'sendPushEdgeFunction', userId })
          pushFailed = true
        }
      } catch (error) {
        void logError(error, { service: 'NotificationService', action: 'sendPush', userId })
      }
    }

    return { notification, pushFailed }
  }

  async sendTeamActivityNotification(
    activityLog: ActivityLog,
    teamMembers: User[],
    user: User,
    activity: Activity,
    activityValue: number,
  ): Promise<{ notifications: Notification[]; pushFailed: boolean }> {
    const otherTeamMembers = teamMembers.filter((member) => member.id !== activityLog.userID)

    const results = await Promise.all(
      otherTeamMembers.map((member) =>
        this.sendNotification({
          userId: member.id,
          type: 'ACTIVITY_LOGGED',
          title: 'New Activity Logged',
          message: activity.requiresAmount
            ? `${user.firstName} logged a ${activityValue} ${activity.unit} ${activity.title}`
            : `${user.firstName} has completed the ${activity.title} activity!`,
          data: {
            activityLogId: activityLog.id,
            activityID: activityLog.activityID,
            teamID: activityLog.teamID,
          },
          sendPush: true,
        }),
      ),
    )

    const notifications = results.map((r) => r.notification)
    const pushFailed = results.some((r) => r.pushFailed)
    return { notifications, pushFailed }
  }

  async getNotifications(userId: string) {
    return dataSource.getNotifications(userId)
  }

  async markAsRead(notification: Notification) {
    await dataSource.saveNotification(notification.markAsRead())
  }

  /** Notify all team members except the user who triggered the earn. */
  async sendTeamBadgeNotification(
    badgeName: string,
    teamMembers: User[],
    excludeUserID: string,
    teamID: string,
    badgeID: string,
    sendPush = true,
  ): Promise<{ notifications: Notification[]; pushFailed: boolean }> {
    const recipients = teamMembers.filter((m) => m.id !== excludeUserID)
    const results = await Promise.all(
      recipients.map((member) =>
        this.sendNotification({
          userId: member.id,
          type: 'TEAM_BADGE_EARNED',
          title: 'Team Badge Earned',
          message: `Your team has earned the ${badgeName}!`,
          data: { teamID: String(teamID), badgeID: String(badgeID) },
          sendPush,
        }),
      ),
    )
    return {
      notifications: results.map((r) => r.notification),
      pushFailed: results.some((r) => r.pushFailed),
    }
  }

  async sendGateReachedNotification(
    gateName: string,
    teamMembers: User[],
    excludeUserID: string,
    teamID: string,
    gateID: string,
  ): Promise<{ notifications: Notification[]; pushFailed: boolean }> {
    return this.sendTeamReachNotification(
      teamMembers,
      excludeUserID,
      'GATE_REACHED',
      'Gate reached',
      `Your team reached ${gateName}. Open the app to complete the gate.`,
      { teamID: String(teamID), gateID: String(gateID) },
    )
  }

  async sendCheckpointReachedNotification(
    checkpointName: string,
    teamMembers: User[],
    excludeUserID: string,
    teamID: string,
    checkpointID: string,
  ): Promise<{ notifications: Notification[]; pushFailed: boolean }> {
    return this.sendTeamReachNotification(
      teamMembers,
      excludeUserID,
      'CHECKPOINT_REACHED',
      'Team progress',
      `Your team reached ${checkpointName}.`,
      { teamID: String(teamID), checkpointID: String(checkpointID) },
    )
  }

  private async sendTeamReachNotification(
    teamMembers: User[],
    excludeUserID: string,
    type: 'GATE_REACHED' | 'CHECKPOINT_REACHED',
    title: string,
    message: string,
    data: Record<string, string>,
  ): Promise<{ notifications: Notification[]; pushFailed: boolean }> {
    const recipients = teamMembers.filter((member) => member.id !== excludeUserID)
    const results = await Promise.all(
      recipients.map((member) =>
        this.sendNotification({
          userId: member.id,
          type,
          title,
          message,
          data,
          sendPush: true,
        }),
      ),
    )
    return {
      notifications: results.map((result) => result.notification),
      pushFailed: results.some((result) => result.pushFailed),
    }
  }

  /**
   * Send one push per teammate when multiple team badges were earned at once.
   * Does not create in-app notifications (those are created by sendTeamBadgeNotification with sendPush: false).
   */
  async sendTeamBadgesBatchedPush(
    badgeCount: number,
    teamMembers: User[],
    excludeUserID: string,
    teamID: string,
  ): Promise<{ pushFailed: boolean }> {
    const recipients = teamMembers.filter((m) => m.id !== excludeUserID)
    const message =
      badgeCount === 1
        ? 'Your team has earned 1 badge!'
        : `Your team has earned ${badgeCount} badges!`
    if (recipients.length === 0) return { pushFailed: false }
    if (Capacitor.getPlatform() === 'android') return { pushFailed: false }

    let pushFailed = false
    for (const member of recipients) {
      const notification = new Notification({
        id: uuidv4(),
        userId: member.id,
        type: 'TEAM_BADGE_EARNED',
        title: 'Team Badges Earned',
        message,
        data: { teamID: String(teamID) },
        read: false,
        createdAt: new Date(),
        sendPush: true,
      })
      try {
        const { data: fnData, error } = await edgeFunctionService.invoke<{ success?: boolean; error?: string }>(
          'send-push-notification',
          { notification: notification.toPG() },
        )
        if (error) void logError(error, { service: 'NotificationService', action: 'sendTeamBadgePush', userId: member.id })
        if (fnData && fnData.success === false) {
          void logError(new Error(fnData.error || 'Unknown error'), { service: 'NotificationService', action: 'sendTeamBadgePushEdgeFunction', userId: member.id })
          pushFailed = true
        }
      } catch (error) {
        void logError(error, { service: 'NotificationService', action: 'sendTeamBadgePush', userId: member.id })
      }
    }
    return { pushFailed }
  }

  async sendChatMessageNotification(message: Message, teamMembers: User[]) {
    const otherTeamMembers = teamMembers.filter((member) => member.id !== message.user.id)

    const results = await Promise.all(
      otherTeamMembers.map((member) =>
        this.sendNotification({
          userId: member.id,
          type: 'TEAM_CHAT',
          title: 'New Chat Message',
          message: `${message.user.firstName}: ${message.message}`,
          data: {
            messageId: String(message.id),
            teamID: String(message.teamID),
            senderId: String(message.user.id),
          },
        }),
      ),
    )

    return results.map((r) => r.notification)
  }
}

export const notificationService = new NotificationService()
